import PaginatedSelect from "@/components/ui/PaginatedSelect";
import { useUser } from "@/context/user-context";

import { useDatasetsFromPipeline } from "@/queries/datasets/useDatasetsFromPipeline";
import { DatasetRead } from "@/types/datasets";
import { UseQueryResult } from "@tanstack/react-query";
import { useHistoryFilters } from "./history-filter-store";

interface DatasetSelectorProps {
  pipelineId?: string;
  promptId?: string;
}

const DatasetSelector = ({ pipelineId, promptId }: DatasetSelectorProps) => {
  const workspaceId = useUser()?.activeWorkspaceId!;

  const filterStore = useHistoryFilters();

  const Query = (
    page: number,
  ): UseQueryResult<
    { datasets: DatasetRead[]; total: number; pages: number },
    unknown
  > | null => {
    const result = useDatasetsFromPipeline(workspaceId, {
      report_id: Number(pipelineId),
      prompt_id: Number(promptId),
      page,
      per_page: 10,
    });

    if (!result.data)
      return result as unknown as UseQueryResult<
        { datasets: DatasetRead[]; total: number; pages: number },
        unknown
      > | null;

    return {
      ...result,
      data: {
        datasets: result.data.datasets,
        total: result.data.total,
      },
    } as unknown as UseQueryResult<
      { datasets: DatasetRead[]; total: number; pages: number },
      unknown
    >;
  };

  return (
    <div>
      <label htmlFor="dataset" className="mb-1 block text-sm font-medium">
        Dataset
      </label>
      <PaginatedSelect<
        DatasetRead,
        { datasets: DatasetRead[]; total: number; pages: number }
      >
        query={Query}
        renderItem={(dataset) =>
          `${dataset.dataset_group?.name} [#${dataset.version_number}]`
        }
        getItemValue={(dataset) => dataset.id.toString()}
        extractData={(data) => ({
          data: data.datasets,
          totalPages: data.pages,
        })}
        placeholder="Select dataset"
        onRemove={() => filterStore.removeDataset()}
        value={
          filterStore.selectedDataset
            ? {
                id: filterStore.selectedDataset.id,
                item: {
                  id: filterStore.selectedDataset.id,
                  name: filterStore.selectedDataset.name,
                },
              }
            : undefined
        }
        onValueChange={(value) => {
          filterStore.removeDataset();
          filterStore.setDataset({
            id: value.id,
            name: `${value.item.dataset_group?.name!} [#${
              value.item.version_number
            }]`,
          });
        }}
      />
    </div>
  );
};

export default DatasetSelector;
